import {useStore} from "vuex";
import { i18n } from "@/scripts/i18n";

// Issue statuses exposed by the Speckle IssueStatus enum.
export const COMMENT_STATUSES = ['open', 'readyForReview', 'resolved'];

// Issue priorities exposed by the Speckle IssuePriority enum.
export const COMMENT_PRIORITIES = ['none', 'low', 'medium', 'high'];

const STATUS_DISPLAY = {
  open          : {labelKey: 'comments.status.open', color: 'orange', icon: 'mdi-circle-outline'},
  readyForReview: {labelKey: 'comments.status.readyForReview', color: 'blue', icon: 'mdi-eye-outline'},
  resolved      : {labelKey: 'comments.status.resolved', color: 'green', icon: 'mdi-check'},
};

// Fallback swatch when a label comes back without a colour.
const DEFAULT_LABEL_COLOR = '#9e9e9e';

const PRIORITY_DISPLAY = {
  none  : {labelKey: 'comments.priority.none', color: 'grey', icon: 'mdi-minus'},
  low   : {labelKey: 'comments.priority.low', color: 'blue-grey', icon: 'mdi-chevron-down'},
  medium: {labelKey: 'comments.priority.medium', color: 'orange', icon: 'mdi-equal'},
  high  : {labelKey: 'comments.priority.high', color: 'red', icon: 'mdi-chevron-double-up'},
};

// Resolve the display of an arbitrary value: the pickers iterate over COMMENT_STATUSES /
// COMMENT_PRIORITIES, so they need a lookup that is not bound to a comment instance.
export const statusDisplay   = (status) => STATUS_DISPLAY[status] ?? STATUS_DISPLAY.open;
export const priorityDisplay = (priority) => PRIORITY_DISPLAY[priority] ?? PRIORITY_DISPLAY.none;

// A picked due date is sent as local midnight converted to UTC, so the chosen day
// survives being read back with the local date getters in getFormattedDueDate().
export const toDueDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();

// Issue descriptions and replies are ProseMirror documents, not plain strings — this is
// the inverse of addText() below. Blank lines are dropped: a paragraph with no content
// is not a valid ProseMirror node.
export const toDescriptionDoc = (text) => ({
  type   : 'doc',
  content: `${text ?? ''}`.split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => ({type: 'paragraph', content: [{type: 'text', text: line}]}))
});

export class Comment {
  comment;
  content;
  id;
  archived;
  attachments = [];
  author;
  canEditDueDate;
  canEditPriority;
  canEditStatus;
  createdAt;
  dueDate;
  labels      = [];
  priority;
  replies     = []
  status;
  text        = [];
  title;
  updatedAt;

  constructor(comment) {
    this.comment   = comment;
    this.id        = comment.id;
    this.createdAt = comment.createdAt;
    this.updatedAt = comment.updatedAt ?? comment.createdAt;
    this.title     = comment.title ?? null;
    // Comment threads are Issues server-side: issues carry the body in `description`
    // and the author in `author.user`, while legacy comment payloads (e.g. the reply
    // mutation response) use `text` and a flat `author`.
    this.content = comment.text ?? comment.description ?? null;
    this.author  = comment.author?.user?.name ?? comment.author?.name ?? '';
    // Fall back to the legacy 2-state model (archived → resolved) when no status is provided.
    this.status        = comment.status ?? (comment.archived ? 'resolved' : 'open');
    this.archived      = comment.archived ?? this.status === 'resolved';
    this.canEditStatus = comment.permissions?.canEditStatus?.authorized ?? comment.canEditStatus ?? false;

    this.priority        = comment.priority ?? 'none';
    this.canEditPriority = comment.permissions?.canEditPriority?.authorized ?? comment.canEditPriority ?? false;

    this.dueDate        = comment.dueDate ?? null;
    this.canEditDueDate = comment.permissions?.canEditDueDate?.authorized ?? comment.canEditDueDate ?? false;

    // Only issues carry labels; replies and legacy comment payloads have none.
    this.labels = (comment.labels ?? []).map((label) => ({
      id   : label.id,
      name : label.name ?? '',
      color: label.hexColor ?? DEFAULT_LABEL_COLOR,
    }));

    this.addText();
    this.addAttachments();
    this.addReplies()
  }

  addText() {
    if (this.content != null && this.content.doc != null) {
      if (this.content.doc.type === "doc") {
        this.content.doc.content.forEach((content) => {
          if (content.type === "paragraph") {
            // An empty paragraph (a blank line typed in Speckle's own editor) has no content.
            content.content?.forEach((paragraphContent) => {
              this.text.push(paragraphContent.text);
            });
          }
        });
      }
    }
  }

  addAttachments() {
    if (this.content != null && this.content.attachments != null && this.content.attachments.length > 0) {
      this.content.attachments.forEach((attachment) => {
        this.attachments.push({
          id      : attachment.id,
          streamId: attachment.streamId ?? null,
          name    : attachment.fileName
        })
      });
    }
  }

  addReplies() {
    if (Object.hasOwn(this.comment, "replies")
      && Object.hasOwn(this.comment.replies, "items")
      && this.comment.replies.items.length > 0) {

      this.comment.replies.items.forEach((reply) => {
        this.replies.push(new Comment(reply));
      });
    }
  }

  // Replies are paged in by a separate query, so the loaded array can lag behind the
  // count the issue itself reports.
  getReplyCount() {
    return this.comment?.replies?.totalCount ?? this.replies.length;
  }

  getAuthorAcronym() {
    return this.author.split(/\s/).reduce((response, word) => response + word.slice(0, 1), '');
  }

  getStatusDisplay() {
    return statusDisplay(this.status);
  }

  getStatusLabel() {
    return i18n.global.t(this.getStatusDisplay().labelKey);
  }

  getStatusColor() {
    return this.getStatusDisplay().color;
  }

  getStatusIcon() {
    return this.getStatusDisplay().icon;
  }

  getPriorityDisplay() {
    return priorityDisplay(this.priority);
  }

  getPriorityLabel() {
    return i18n.global.t(this.getPriorityDisplay().labelKey);
  }

  getPriorityColor() {
    return this.getPriorityDisplay().color;
  }

  getPriorityIcon() {
    return this.getPriorityDisplay().icon;
  }

  hasLabels() {
    return this.labels.length > 0;
  }

  hasSingleLabel() {
    return this.labels.length === 1;
  }

  getLabelCount() {
    return this.labels.length;
  }

  hasDueDate() {
    return this.dueDate !== null;
  }

  // The due date is stored as local midnight converted to UTC, so local getters
  // give back the intended day; toISOString() would shift it by one day for
  // timezones west of the one that set it.
  getFormattedDueDate() {
    const dueDate = new Date(this.dueDate);
    const month   = this.padValue(dueDate.getMonth() + 1);
    const date    = this.padValue(dueDate.getDate());
    return `${dueDate.getFullYear()}-${month}-${date}`;
  }

  isOverdue() {
    return this.hasDueDate() && new Date(this.dueDate) < new Date() && this.status !== 'resolved';
  }

  getDueDateColor() {
    if (this.isOverdue()) {
      return 'red';
    }
    const twoDays = 2 * 24 * 60 * 60 * 1000;
    if (this.hasDueDate() && this.status !== 'resolved' && new Date(this.dueDate).getTime() - Date.now() <= twoDays) {
      return 'orange';
    }
    return 'grey';
  }

  getDueDateIcon() {
    return this.isOverdue() ? 'mdi-calendar-alert' : 'mdi-calendar';
  }

  isResolved() {
    return this.status === 'resolved';
  }

  getFormattedCommentCreatedDate() {
    const today       = new Date();
    const commentDate = new Date(this.createdAt);
    const createdAt   = new Date(this.createdAt);

    if (today.setHours(0, 0, 0, 0) === commentDate.setHours(0, 0, 0, 0)) {
      const hours   = this.padValue(createdAt.getHours());
      const minutes = this.padValue(createdAt.getMinutes());
      return `${i18n.global.t('today').capitalize()} ${hours}:${minutes}`;
    }

    const month   = this.padValue(createdAt.getMonth() + 1);
    const date    = this.padValue(createdAt.getDate());
    const hours   = this.padValue(createdAt.getHours());
    const minutes = this.padValue(createdAt.getMinutes());

    return `${createdAt.getFullYear()}-${month}-${date} ${hours}:${minutes}`;
  }

  getStringifiedText() {
    if (this.content != null && this.text.length < 1) {
      return this.getStringifiedAttachment();
    }
    // eslint-disable-next-line no-debugger
    const texts = this.text.join('\r\n');
    let content = [
      texts,
      this.getStringifiedAttachment()
    ];
    return content.join('<br>');
  }

  getStringifiedAttachment() {
    if (this.attachments.length < 1) {
      return '';
    }

    const store = useStore();

    const attachmentRoot = `https://app.speckle.systems/api/stream/${store.state.projectData?.projectId}/blob`
    let attachmentArray  = [];
    this.attachments.forEach((attachment) => {
      attachmentArray.push(`<a href=${attachmentRoot}/${attachment.id} target="_blank">${attachment.name}</a>`)
    });
    return attachmentArray.join('\r\n');
  }

  padValue(number) {
    return number.toString().padStart(2, '0');
  }
}